import { ErrorCode, WarningCode } from '../generated/errorCodes.gen';

// #132 — the manual reporting-period create flow (POST /api/finance/deadlines/manual,
// #121). A treasurer in a jurisdiction whose calendar the app can't fully auto-generate
// (Virginia's election-relative periods) enters a coverage window + due date by hand. The
// old deadlines-create screen was removed (FIP-001); this is the sole create surface.
//
// The FE switches on the generated `ErrorCode` and renders `manualDeadlineErrorCopy` so a
// 400 becomes treasurer-readable copy, never a raw code.

/**
 * Treasurer-facing copy for a manual reporting-period ADVISORY (not an error — the period
 * was created). `reportCode` is the code the treasurer typed, woven into the message so it
 * names the offending value. An unknown warning code returns null so the caller can choose
 * to show nothing rather than a misleading generic line. Warn-don't-block: the copy nudges
 * ("double-check"), it never claims the code is invalid — the treasurer may know one we don't.
 */
export function manualDeadlineWarningCopy(
  code: string | null | undefined,
  reportCode?: string | null,
): string | null {
  switch (code) {
    case WarningCode.ManualPeriodUnrecognizedReportCode: {
      const quoted = reportCode?.trim() ? `“${reportCode.trim()}” ` : '';
      return `Heads up: ${quoted}isn’t a report code we recognize for this jurisdiction. The reporting period was created — double-check the code against your filing calendar before you file.`;
    }
    default:
      return null;
  }
}

/**
 * Treasurer-facing copy for a manual reporting-period validation failure. Renders the
 * server's contiguity / non-overlap / range verdict in plain language at the boundary —
 * an unrecognized code falls back to a generic (never-blank) message so a new backend
 * code can't surface as an empty error.
 */
export function manualDeadlineErrorCopy(code: string | null | undefined): string {
  switch (code) {
    case ErrorCode.ManualPeriodMissingName:
      return 'Give this reporting period a name.';
    case ErrorCode.ManualPeriodInvalidRange:
      return "Check the dates — the coverage start must be on or before the end, and the due date can't fall before the period closes.";
    case ErrorCode.ManualPeriodOverlap:
      return 'This coverage window overlaps an existing reporting period. Adjust the dates so the periods don’t overlap.';
    case ErrorCode.ManualPeriodNonContiguous:
      return 'This period leaves a gap after the previous one. Reporting periods must be contiguous — each one starts the day after the last period ends.';
    default:
      return "We couldn't create this reporting period. Check the dates and try again.";
  }
}
